import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { committeeSummary, HANDOVER_ITEMS } from './analytics';
import type { TermLike, CommitteeMemberLike, SecretaryHandoverLike } from './analytics';

export interface CommitteeSummaryProps {
  terms: TermLike[];
  members: CommitteeMemberLike[];
  handovers: SecretaryHandoverLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

const when = (days: number | null) =>
  days === null ? '-' : days < 0 ? `${-days} days ago` : days === 0 ? 'Today' : `in ${days} days`;

/** The Committee History Summary tab: tiles, then report panels and watch-lists. */
const CommitteeSummaryView = ({ terms, members, handovers, loading, isDarkMode }: CommitteeSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => committeeSummary(terms, members, handovers), [terms, members, handovers]);
  const termPeak = peakOf(s.terms, (r) => r.members);
  const servedPeak = peakOf(s.mostServed, (r) => r.terms);
  const sub = { fontSize: '12px', color: t.textFaint };

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Current Term" value={s.current?.name || '—'} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard
          label="Days Left in Term"
          value={s.daysLeft === null ? '—' : String(s.daysLeft)}
          color={s.daysLeft !== null && s.daysLeft <= 90 ? t.warning : t.income}
          isDarkMode={isDarkMode}
        />
        <KpiCard label="Term Served" value={s.elapsedPct === null ? '—' : `${s.elapsedPct}%`} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Serving Members" value={String(s.serving.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Office Bearers" value={String(s.officeBearers.length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Terms Recorded" value={String(s.terms.length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="People Who Served" value={String(s.people)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Handover Checklist" value={s.handovers.length ? `${s.checklistPct}%` : '—'} color={s.checklistPct === 100 || !s.handovers.length ? t.income : t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Pending Handovers" value={String(s.pending.length)} color={s.pending.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Needs Attention" value={String(s.alerts.length)} color={s.alerts.length ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title={s.current ? `Office Bearers — ${s.current.name}` : 'Office Bearers'}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'role', header: 'Office', render: (r) => <strong>{r.role || '-'}</strong> },
            { key: 'name', header: 'Name', render: (r) => r.name || '-' },
            { key: 'fromDate', header: 'Since', nowrap: true, render: (r) => formatDate(r.fromDate) }
          ]}
          rows={s.officeBearers}
          rowKey={(r) => `${r.role}|${r.name}`}
          emptyMessage={s.current ? 'No office bearers recorded for this term.' : 'No term is marked Active.'}
        />
        <TablePanel
          title="Serving Members by Role"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Role', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, s.serving.length)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.byRole}
          rowKey="name"
          emptyMessage="No one is serving in an active term."
          below={s.byRole.length ? <TotalRow label="Total serving" value={s.serving.length} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Terms"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'name', header: 'Term', render: (r) => (<><strong>{r.name}</strong><br /><span style={sub}>{r.recordId}</span></>) },
          { key: 'dates', header: 'Period', nowrap: true, render: (r) => `${formatDate(r.startDate)} – ${formatDate(r.endDate)}` },
          { key: 'status', header: 'Status', render: (r) => <Badge tone={r.status === 'Active' ? 'success' : 'neutral'}>{r.status}</Badge> },
          { key: 'bar', header: 'Members', render: (r) => <BarCell value={r.members} max={termPeak} color={t.info} isDarkMode={isDarkMode} /> },
          { key: 'members', header: '', align: 'right' }
        ]}
        rows={s.terms}
        rowKey={(r) => `${r.recordId}|${r.startDate}`}
        emptyMessage="No terms recorded yet."
        below={s.terms.length ? <TotalRow label="Member records across all terms" value={members.length} isDarkMode={isDarkMode} /> : null}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Members by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, members.length)} max={100} showPercent color={r.name === 'Active' ? t.income : r.name === 'Deceased' ? t.danger : t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Records', align: 'right' }
          ]}
          rows={s.byStatus}
          rowKey="name"
          emptyMessage="No committee members recorded yet."
          below={s.byStatus.length ? <TotalRow label="Total records" value={members.length} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Longest Serving — Terms Served"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Name', render: (r) => (<><strong>{r.name}</strong><br /><span style={sub}>{r.roles}</span></>) },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.terms} max={servedPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'terms', header: 'Terms', align: 'right' }
          ]}
          rows={s.mostServed.slice(0, 10)}
          rowKey="name"
          emptyMessage="No committee members recorded yet."
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Secretary Handover Checklist"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Item', render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: 'Handed Over', render: (r) => <BarCell value={percent(r.done, s.handovers.length)} max={100} showPercent color={r.done === s.handovers.length ? t.income : t.warning} isDarkMode={isDarkMode} /> },
            { key: 'done', header: 'Of', align: 'right', nowrap: true, render: (r) => `${r.done} / ${s.handovers.length}` }
          ]}
          rows={s.handovers.length ? s.items : []}
          rowKey="key"
          emptyMessage="No secretary handovers recorded yet."
          below={s.handovers.length
            ? <TotalRow label={`Overall (${HANDOVER_ITEMS.length} items × ${s.handovers.length})`} value={`${s.checklistPct}%`} color={s.checklistPct === 100 ? t.income : t.warning} isDarkMode={isDarkMode} />
            : null}
        />
        <TablePanel
          title="Needs Attention"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'kind', header: 'What', render: (r) => <Badge tone={r.days !== null && r.days < 0 && r.kind.startsWith('Term') ? 'error' : 'warning'}>{r.kind}</Badge> },
            { key: 'subject', header: 'Detail', wrap: true, render: (r) => <strong>{r.subject}</strong> },
            { key: 'date', header: 'Date', nowrap: true, render: (r) => (<>{formatDate(r.date)}<br /><span style={sub}>{when(r.days)}</span></>) }
          ]}
          rows={s.alerts}
          rowKey="key"
          emptyMessage="Nothing needs attention."
        />
      </SummaryColumns>
    </>
  );
};

export default CommitteeSummaryView;
